import React, { useRef, useEffect } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  HeroSection,
  HeroContent,
  HeroText,
  HeroImage,
  EyebrowText
} from "./HeroStyles";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const imageAnimation = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

export default function HeroAlternative({ 
  title = "Welcome to August Twenty",
  description = null,
  eyebrow = null,
  imageSrc = "/girl_looking.jpg",
  showButton = true,
  buttonText = "Contact Us",
  buttonLink = "/contact-us", // Changed to /contact-us
  altText = null
}) {
  const heroRef = useRef(null);
  const heroControls = useAnimation();
  const heroInView = useInView(heroRef, { once: true, amount: 0.2 });

  useEffect(() => {
    if (heroInView) heroControls.start("visible");
  }, [heroInView, heroControls]);

  const handleButtonClick = () => {
    if (buttonLink) {
      window.location.href = buttonLink;
    }
  };

  return (
    <HeroSection id="hero-section" ref={heroRef}>
      <HeroContent>
        <HeroText>
          <motion.div
            initial="hidden"
            animate={heroControls}
            variants={staggerContainer}
          >
            {eyebrow && (
              <motion.div variants={fadeInUp}>
                <EyebrowText>{eyebrow}</EyebrowText>
              </motion.div>
            )}
            
            <motion.h1 variants={fadeInUp}>{title}</motion.h1>
            
            {description && (
              <motion.p variants={fadeInUp}>{description}</motion.p>
            )}
            
            {showButton && (
              <motion.div variants={fadeInUp}>
                {/* Wrap button in an anchor tag */}
                <a href={buttonLink} style={{ textDecoration: 'none' }}>
                  <button>
                    {buttonText}
                    <div className="arrow-container">
                      <ArrowUpRight size={20} />
                    </div>
                  </button>
                </a>
              </motion.div>
            )}
          </motion.div>
        </HeroText>

        <motion.div
          initial="hidden"
          animate={heroControls}
          variants={imageAnimation}
        >
          <HeroImage>
            <img 
              src={imageSrc} 
              alt={altText || title} 
              loading="eager"
            />
          </HeroImage>
        </motion.div>
      </HeroContent>
    </HeroSection>
  );
}